<script setup lang="ts">
import Card from 'primevue/card';
import Button from 'primevue/button';
import DefaultChip from '../components/DefaultChip.vue';

import CalculationStepperSummary from '../components/CalculationStepperSummary.vue';

import { useCalculatorsStore } from '../store/calculators';

import { useCurrentClientCalculations } from '../composables/useCurrentClientCalculations';
import { useCurrentCalculatorSegments } from '../composables/useCurrentCalculatorSegments';

const { currentClientCalculations } = useCurrentClientCalculations();
const { getSegmentNameById, getSegmentFirstGroupId } = useCurrentCalculatorSegments();
const calculatorsStore = useCalculatorsStore();
</script>

<template>
  <Card class="border border-gray-200 !shadow-none">
    <template #content>
      <section class="">
        <div class="">
          <RouterLink
            :to="{
              name: 'ClientView',
              params: {
                calculatorId: $route.params.calculatorId,
                clientId: $route.params.clientId || NaN,
              },
            }"
          >
            <article class="flex items-center gap-3">
              <div
                class="flex aspect-square w-10 items-center justify-center rounded-lg border transition-colors"
                :class="{
                  'border-gray-200 bg-white':
                    $route.name !== 'NewClientView' && $route.name !== 'ClientView',
                  'border-brand-200 bg-brand-25':
                    $route.name === 'NewClientView' || $route.name === 'ClientView',
                }"
              >
                <i class="pi pi-user-plus text-gray-700" />
              </div>

              <h2 class="font-semibold text-gray-900">Dane klienta</h2>
            </article>
          </RouterLink>

          <div class="my-1 ml-[19px] h-5 w-0.5 rounded-sm bg-gray-200"></div>

          <article class="flex items-center gap-3">
            <div
              class="flex aspect-square w-10 items-center justify-center rounded-lg border"
              :class="{
                'border-gray-200 bg-white':
                  $route.name === 'NewClientView' || $route.name === 'ClientView',
                'border-brand-200 bg-brand-25':
                  $route.name !== 'NewClientView' && $route.name !== 'ClientView',
              }"
            >
              <i class="pi pi-calculator" />
            </div>

            <h2 class="font-semibold text-gray-900">Kalkulacje</h2>
          </article>
        </div>

        <template :key="calculation.uuid" v-for="calculation in currentClientCalculations">
          <div class="my-1 ml-[19px] h-5 w-0.5 rounded-sm bg-gray-200"></div>

          <Card
            class="basis-[354px] border !shadow-none"
            :class="{
              'border-gray-200': calculation.uuid !== $route.params.calculationId,
              'border-brand-200 !bg-brand-25': calculation.uuid === $route.params.calculationId,
            }"
          >
            <template #content>
              <div class="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                <i class="pi pi-chevron-right" />

                <RouterLink
                  v-if="calculation"
                  :to="{
                    name: 'CalculationSummaryView',
                    params: {
                      calculatorId: calculation.calculator_id,
                      calculationId: calculation.uuid,
                      clientId: calculation.client_uuid,
                    },
                  }"
                >
                  <span class="hover:underline">
                    {{ calculation.name }} {{ calculation.calculator_id }}
                  </span>
                </RouterLink>

                <DefaultChip
                  class="ml-auto"
                  :variant="
                    Number($route.params.calculatorId) === calculation.calculator_id
                      ? 'yellow'
                      : 'gray'
                  "
                >
                  {{
                    calculatorsStore.data?.find(
                      (calculator) => calculator.id === calculation.calculator_id
                    )?.version
                  }}
                </DefaultChip>
              </div>

              <template v-if="calculation.calculator_id === Number($route.params.calculatorId)">
                <RouterLink
                  :key="segment.uuid"
                  v-for="segment in calculation.calculation_segments"
                  class="flex items-center gap-2 text-sm text-gray-700 hover:underline"
                  :to="{
                    name: 'CalculationViewItems',
                    params: {
                      ...$route.params,
                      calculationId: calculation.uuid,
                      segmentId: segment.segment_id,
                      viewId: getSegmentFirstGroupId(segment.segment_id)?.viewId,
                      groupId: getSegmentFirstGroupId(segment.segment_id)?.groupId,
                    },
                  }"
                >
                  <div class="gorder-gray-200 h-6 w-2.5 -translate-y-3 border-b-2 border-l-2"></div>

                  {{ getSegmentNameById(segment.segment_id) }}
                </RouterLink>
              </template>
              <div v-else>
                <p class="text-sm text-gray-600">
                  Kalkulacja należy do innej wersji kalkulatora. Zmień wersję kalkulatora, aby
                  zobaczyć technologie.
                </p>
              </div>

              <RouterLink
                :to="{
                  name: 'SelectSegmentView',
                  params: {
                    calculatorId: calculation.calculator_id,
                    clientId: $route.params.clientId,
                    calculationId: calculation.uuid,
                  },
                }"
              >
                <Button class="mt-4 w-full font-semibold" size="small" outlined>
                  <i class="pi pi-plus" />
                  <span class="my-auto self-stretch px-0.5">Dodaj technologię</span>
                </Button>
              </RouterLink>
            </template>
          </Card>
        </template>

        <RouterLink
          v-if="$route.params.clientId && $route.params.calculatorId"
          :to="{
            name: 'NewCalculationView',
            params: {
              calculatorId: $route.params.calculatorId,
              clientId: $route.params.clientId,
            },
          }"
        >
          <Button class="mt-4 w-full font-semibold" size="small">
            <i class="pi pi-plus" />
            <span class="my-auto self-stretch px-0.5">Dodaj kalkulacje</span>
          </Button>
        </RouterLink>

        <CalculationStepperSummary v-if="$route.params.calculationId" v-auto-animate class="mt-6" />
      </section>
    </template>
  </Card>
</template>
